import { useState } from "react";
import { Button } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

/**
 * RefreshButton — one consistent, attractive Refresh control for the whole
 * app, replacing 9 differently-styled Refresh buttons scattered across
 * Dashboard, Candidates, JobRequirements, Pipeline-adjacent pages, My Day,
 * Tickets, and the 3 Client-facing pages.
 *
 * SAFETY DESIGN NOTE: this component does NOT change what happens when you
 * click Refresh on any page. It calls the exact same `onClick` function
 * each page already passed to its old <Button> — unchanged, same
 * reference, same behavior, same API calls.
 *
 * Why the spin is time-based rather than tied to the real request: across
 * the 8 pages using this, refresh handlers are inconsistent under the
 * hood — some return a promise, some don't; one page already toggles its
 * own `loading` state on refresh, most don't. Making every page's fetch
 * function return a trackable promise (or expose a loading flag) just to
 * power this button would mean touching 8 files' real data-loading logic —
 * exactly the kind of change with real regression risk. Instead, this
 * button gives honest, self-contained visual feedback ("yes, your click
 * registered") via a brief spin + disabled state, independent of how long
 * the underlying request actually takes. This is a common, low-risk
 * pattern and avoids touching any existing fetch/loading logic at all.
 *
 * Props:
 *   onClick - the page's existing refresh handler, unchanged (required)
 *   label   - button text (default "Refresh")
 *   size    - MUI size (default "small")
 */
export default function RefreshButton({
  onClick,
  label = "Refresh",
  size = "small",
  ...rest
}) {
  const [spinning, setSpinning] = useState(false);

  const handleClick = (e) => {
    if (spinning) return; // guards against rapid double-clicks
    setSpinning(true);
    onClick?.(e);
    // Fixed, short duration — long enough to read as "it happened", short
    // enough to not feel laggy. Does not wait on the actual request.
    setTimeout(() => setSpinning(false), 700);
  };

  return (
    <Button
      variant="outlined"
      size={size}
      onClick={handleClick}
      disabled={spinning}
      startIcon={
        <RefreshIcon
          fontSize={size === "small" ? "small" : "medium"}
          sx={{
            animation: spinning ? "rb-spin 0.7s linear" : "none",
            "@keyframes rb-spin": {
              from: { transform: "rotate(0deg)" },
              to: { transform: "rotate(360deg)" },
            },
          }}
        />
      }
      sx={{
        borderRadius: "8px",
        textTransform: "none",
        fontWeight: 600,
        borderColor: "divider",
        color: "text.secondary",
        "&:hover": {
          borderColor: "primary.main",
          color: "primary.main",
          bgcolor: "action.hover",
        },
      }}
      {...rest}
    >
      {label}
    </Button>
  );
}
